import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { isWellFormedPin, normalisePinUsername } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Giving somebody a PIN for the counter. `TEN-006`, `decisions/0021`.
 *
 * One person's panel inside the people list on the setup screen, a separate component so that
 * screen does not grow a third mode in an already long conditional. The caller decides who gets
 * one: somebody the server would not refuse (a role that may hold a PIN, who still works here, at
 * a branch the caller holds, on a deployment with PIN sign-in on). A refused write halts the write
 * queue, so a door that leads to a refusal is not offered.
 *
 * **The checks here are the server's own, run first.** `normalisePinUsername` and `isWellFormedPin`
 * are the domain functions the service calls, so the screen and the route cannot disagree about
 * what a short name or a PIN is.
 *
 * **The PIN is typed in the clear on purpose, for `ShopSetup`'s password reason.** It is chosen for
 * somebody else and read out to them across a counter. It is dropped from state the moment the
 * write is queued.
 */

export interface PinSetupProps {
  readonly staffId: string
  readonly displayName: string
  /** Their short name today, or null when they have no PIN. Never the PIN. */
  readonly pinUsername: string | null
  /** Digits in a PIN, from the deployment. */
  readonly pinLength: number
  /** Called with the queued write's id, so the parent can refresh when the server has taken it. */
  readonly onQueued: (writeId: string) => void
}

export function PinSetup({
  staffId,
  displayName,
  pinUsername,
  pinLength,
  onQueued,
}: PinSetupProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue } = useWriteQueue()
  const [open, setOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)

  const save = useCallback(async () => {
    const shortName = normalisePinUsername(username)
    if (shortName === null) {
      setError(t('shopSetup.pin.usernameInvalid'))
      return
    }
    if (!isWellFormedPin(pin, pinLength)) {
      setError(t('shopSetup.pin.pinInvalid', { count: pinLength }))
      return
    }
    setError(null)
    const write = (await enqueue({
      method: 'POST',
      path: `/staff/${encodeURIComponent(staffId)}/pin`,
      label: t('shopSetup.pin.queueLabel', { name: displayName }),
      body: { username: shortName, pin },
    })) as { id: string }
    setPin('')
    setOpen(false)
    onQueued(write.id)
  }, [displayName, enqueue, onQueued, pin, pinLength, staffId, t, username])

  if (!open) {
    return (
      <>
        {pinUsername === null ? null : (
          <span className="lm-setup-person__meta" dir="ltr">
            {t('shopSetup.pin.current', { username: pinUsername })}
          </span>
        )}
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setOpen(true)
            setUsername(pinUsername ?? '')
            setPin('')
            setError(null)
          }}
        >
          {pinUsername === null
            ? t('shopSetup.pin.set', { name: displayName })
            : t('shopSetup.pin.change', { name: displayName })}
        </button>
      </>
    )
  }

  return (
    <div className="lm-setup-person__change">
      <label className="lm-field">
        <span className="lm-field__label">{t('shopSetup.pin.username')}</span>
        <input
          className="lm-input"
          dir="ltr"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
          value={username}
          onChange={(e) => setUsername(e.currentTarget.value)}
        />
      </label>
      <label className="lm-field">
        <span className="lm-field__label">{t('shopSetup.pin.pin', { count: pinLength })}</span>
        <input
          className="lm-input"
          dir="ltr"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.currentTarget.value)}
        />
      </label>
      <p className="lm-setup-panel__effects">{t('shopSetup.pin.means', { name: displayName })}</p>
      <button
        type="button"
        className="lm-button lm-button--primary"
        onClick={() => {
          void save()
        }}
      >
        {t('shopSetup.pin.confirm')}
      </button>
      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setOpen(false)
          setPin('')
        }}
      >
        {t('shopSetup.pin.cancel')}
      </button>
      {error === null ? null : <p className="lm-error">{error}</p>}
    </div>
  )
}
